import React, { useEffect, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Search, TrendingUp, Clock, CheckCircle2, Circle, RotateCcw } from 'lucide-react';
import { fetchUserStates } from '../api/progress';
import { fetchWords } from '../api/words';
import type { Word, UserWordState } from '../types';

export const ProgressPage: React.FC = () => {
  const [words, setWords] = useState<Word[]>([]);
  const [states, setStates] = useState<Map<string, UserWordState>>(new Map());
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'new' | 'learning' | 'mastered'>('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    Promise.all([fetchWords(), fetchUserStates()])
      .then(([wordList, stateList]) => {
        setWords(wordList);
        const map = new Map<string, UserWordState>();
        for (const s of stateList) {
          map.set(s.word_id, s);
        }
        setStates(map);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const stats = useMemo(() => {
    const newCount = words.filter((w) => !states.has(w.id) || states.get(w.id)?.status === 'new').length;
    const learningCount = words.filter((w) => {
      const s = states.get(w.id);
      return s && ['learning', 'review', 'relearning'].includes(s.status);
    }).length;
    const masteredCount = words.filter((w) => states.get(w.id)?.status === 'mastered').length;
    return { newCount, learningCount, masteredCount, total: words.length };
  }, [words, states]);

  const filteredWords = useMemo(() => {
    let filtered = words;
    if (filter !== 'all') {
      filtered = words.filter((w) => {
        const s = states.get(w.id);
        if (filter === 'new') return !s || s.status === 'new';
        if (filter === 'learning') return s && ['learning', 'review', 'relearning'].includes(s.status);
        if (filter === 'mastered') return s?.status === 'mastered';
        return true;
      });
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      filtered = filtered.filter((w) => w.word.toLowerCase().includes(q));
    }
    return filtered;
  }, [words, states, filter, search]);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="flex items-center gap-3 text-text-dim">
          <div className="w-4 h-4 border-2 border-white/10 border-t-gold rounded-full animate-spin" />
          Loading progress...
        </div>
      </div>
    );
  }

  const total = stats.total || 1;

  return (
    <div className="p-4 md:p-8 pb-24 md:pb-8 max-w-5xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-5"
      >
        {/* Mastery Ring */}
        <div className="glass-card p-6 rounded-2xl flex flex-col md:flex-row items-center gap-6">
          <div className="relative w-36 h-36 shrink-0">
            <svg width="144" height="144" viewBox="0 0 144 144" className="-rotate-90">
              {(() => {
                const r = 56;
                const c = 2 * Math.PI * r;
                const newPct = stats.newCount / total;
                const learningPct = stats.learningCount / total;
                const masteredPct = stats.masteredCount / total;
                return (
                  <>
                    <circle cx="72" cy="72" r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="10" />
                    <circle cx="72" cy="72" r={r} fill="none" stroke="#3e4260" strokeWidth="10"
                      strokeDasharray={c} strokeDashoffset={c - c * newPct} strokeLinecap="round" />
                    <circle cx="72" cy="72" r={r} fill="none" stroke="#e09050" strokeWidth="10"
                      strokeDasharray={c} strokeDashoffset={c - c * learningPct} strokeLinecap="round"
                      style={{ transform: `rotate(${newPct * 360}deg)`, transformOrigin: '72px 72px' }} />
                    <circle cx="72" cy="72" r={r} fill="none" stroke="#3db48a" strokeWidth="10"
                      strokeDasharray={c} strokeDashoffset={c - c * masteredPct} strokeLinecap="round"
                      style={{ transform: `rotate(${(newPct + learningPct) * 360}deg)`, transformOrigin: '72px 72px' }} />
                  </>
                );
              })()}
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="font-mono text-2xl text-cream">{stats.masteredCount}</span>
              <span className="text-[10px] text-text-dim uppercase tracking-widest">Mastered</span>
            </div>
          </div>

          <div className="flex-1 w-full">
            <h2 className="font-display text-xl text-cream mb-4">Mastery Breakdown</h2>
            <div className="space-y-3">
              <MasteryBar label="New" count={stats.newCount} total={total} color="bg-text-dim" />
              <MasteryBar label="Learning" count={stats.learningCount} total={total} color="bg-amber" />
              <MasteryBar label="Mastered" count={stats.masteredCount} total={total} color="bg-green" />
            </div>
          </div>
        </div>

        {/* Word list */}
        <div className="glass-card p-5 rounded-2xl">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
            <h3 className="text-sm font-semibold text-text-hi flex items-center gap-2">
              <TrendingUp size={14} className="text-gold" />
              Word List
            </h3>
            <div className="flex items-center gap-2">
              <div className="relative flex-1 md:w-56">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-dim" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search words..."
                  className="w-full bg-white/[0.04] border border-border rounded-lg pl-9 pr-3 py-2 text-sm text-cream placeholder:text-text-dim outline-none focus:border-gold transition-colors"
                />
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
            {(['all', 'new', 'learning', 'mastered'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all duration-150 capitalize whitespace-nowrap ${
                  filter === f
                    ? 'border-gold-dim text-gold bg-gold-glow'
                    : 'border-border text-text-dim hover:text-text-hi hover:bg-surface-1'
                }`}
              >
                {f}
              </button>
            ))}
          </div>

          <div className="space-y-1 max-h-[50vh] overflow-y-auto pr-1">
            {filteredWords.length === 0 ? (
              <div className="text-center py-8 text-text-dim text-sm">No words match your filters.</div>
            ) : (
              filteredWords.map((word) => {
                const state = states.get(word.id);
                return (
                  <WordRow key={word.id} word={word} state={state} />
                );
              })
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const MasteryBar: React.FC<{ label: string; count: number; total: number; color: string }> = ({
  label, count, total, color,
}) => {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-text-dim w-16 font-medium">{label}</span>
      <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
        <div className={`h-full rounded-full transition-all duration-500 ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-mono text-text w-8 text-right">{count}</span>
    </div>
  );
};

const WordRow: React.FC<{ word: Word; state?: UserWordState }> = ({ word, state }) => {
  const accuracy = state && state.accuracy_history.length > 0
    ? Math.round((state.accuracy_history.filter(Boolean).length / state.accuracy_history.length) * 100)
    : null;

  const avgResponseTime = state && state.response_time_ms.length > 0
    ? Math.round(state.response_time_ms.reduce((a, b) => a + b, 0) / state.response_time_ms.length / 1000)
    : null;

  const status = state?.status || 'new';
  const statusIcon = status === 'mastered'
    ? <CheckCircle2 size={14} className="text-green" />
    : status === 'new'
    ? <Circle size={14} className="text-text-dim" />
    : <RotateCcw size={14} className="text-amber" />;

  return (
    <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-surface-1 transition-colors">
      <div className="shrink-0">{statusIcon}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm text-text-hi font-medium truncate">{word.word}</span>
          <span className="text-[10px] text-text-dim uppercase tracking-wider">{word.part_of_speech}</span>
        </div>
        <p className="text-xs text-text truncate">{word.definition}</p>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        {accuracy !== null && (
          <div className="flex items-center gap-1 text-xs text-text-dim">
            <TrendingUp size={12} />
            <span className="font-mono">{accuracy}%</span>
          </div>
        )}
        {avgResponseTime !== null && (
          <div className="flex items-center gap-1 text-xs text-text-dim">
            <Clock size={12} />
            <span className="font-mono">{avgResponseTime}s</span>
          </div>
        )}
        <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${
          status === 'mastered'
            ? 'text-green border-green/20 bg-green-muted'
            : status === 'new'
            ? 'text-text-dim border-white/10 bg-white/[0.04]'
            : 'text-amber border-amber/20 bg-amber-muted'
        }`}>
          {status}
        </span>
      </div>
    </div>
  );
};
